/**
 * Query APIs for Government Intake.
 * Backend: os.routes.ts — GET list, GET by id, GET by-ids under /v0/os/government_intakes.
 */

import axios from 'axios';
import { HttpMethods } from '@/api/base';
import { BASE_PATH } from './constants';
import type {
  GetGovernmentIntakeResponse,
  GetGovernmentIntakeService,
  GetGovernmentIntakesByIdsResponse,
  GetGovernmentIntakesByIdsService,
  GetGovernmentIntakesResponse,
  GetGovernmentIntakesService,
} from './types';

/** Backend os.routes.ts: GET /v0/os/government_intakes — list intakes (optional pagination/filters). */
export async function getGovernmentIntakeList(params?: GetGovernmentIntakesService['params']): Promise<GetGovernmentIntakesResponse> {
  const res = await axios.request<GetGovernmentIntakesResponse>({
    method: HttpMethods.GET,
    url: BASE_PATH,
    params: params ?? undefined,
  });
  return res.data;
}

/** Same as getGovernmentIntakeList; used by factory (backend os.routes.ts). */
export async function getGovernmentIntakes(params?: GetGovernmentIntakesService['params']): Promise<GetGovernmentIntakesResponse> {
  return getGovernmentIntakeList(params);
}

/** Backend os.routes.ts: GET /v0/os/government_intakes/:id — fetch one intake. */
export async function getGovernmentIntake({ intakeId }: GetGovernmentIntakeService): Promise<GetGovernmentIntakeResponse> {
  const res = await axios.request<GetGovernmentIntakeResponse>({
    method: HttpMethods.GET,
    url: `${BASE_PATH}/${intakeId}`,
  });
  return res.data;
}

/** Backend os.routes.ts: GET /v0/os/government_intakes/list/by-account-ids — list intakes where accountId in ids (same shape as list API). */
export async function getGovernmentIntakesByIds({
  intakeId,
  accountIds,
}: GetGovernmentIntakesByIdsService): Promise<GetGovernmentIntakesByIdsResponse> {
  const idsToUse = accountIds ?? intakeId ?? [];
  if (idsToUse.length === 0) {
    return { data: [] };
  }
  const ids = idsToUse.join(',');
  const res = await axios.request<GetGovernmentIntakesByIdsResponse>({
    method: HttpMethods.GET,
    url: `${BASE_PATH}/list/by-account-ids`,
    params: { ids },
  });
  return res.data;
}

/** Backend os.routes.ts: GET /system-catalog — list system catalog entries. */
export type SystemCatalogListItem = {
  id: number;
  name: string;
  vendor: string | null;
  category: string | null;
  aliases: unknown;
  isActive: boolean;
};

export type GetSystemCatalogListResponse = {
  data: SystemCatalogListItem[];
};

export async function getSystemCatalogList(params?: { search?: string; isActive?: boolean }): Promise<GetSystemCatalogListResponse> {
  const res = await axios.request<GetSystemCatalogListResponse>({
    method: HttpMethods.GET,
    url: `${BASE_PATH}/system-catalog`,
    params: params ?? undefined,
  });
  return res.data;
}

/** Backend os.routes.ts: POST /system-catalog — create a system catalog entry. */
export type CreateSystemCatalogPayload = {
  name: string;
  vendor?: string;
  category?: string;
  aliases?: string[];
  isActive?: boolean;
  createdById?: number | null;
  updatedById?: number | null;
};

export type CreateSystemCatalogResponse = {
  data: {
    id: number;
    name: string;
    vendor: string | null;
    category: string | null;
    aliases: unknown;
    isActive: boolean;
  };
};

export async function createSystemCatalog(payload: CreateSystemCatalogPayload): Promise<CreateSystemCatalogResponse> {
  const res = await axios.request<CreateSystemCatalogResponse>({
    method: HttpMethods.POST,
    url: `${BASE_PATH}/system-catalog`,
    data: payload,
  });
  return res.data;
}

/** Backend os.routes.ts: PATCH /system-catalog/:id — update a system catalog entry. */
export type PatchSystemCatalogPayload = {
  name?: string;
  vendor?: string;
  category?: string;
  aliases?: string[];
  isActive?: boolean;
  updatedById?: number | null;
};

export type PatchSystemCatalogResponse = {
  data: {
    id: number;
    name: string;
    vendor: string | null;
    category: string | null;
    aliases: unknown;
    isActive: boolean;
  };
};

export async function patchSystemCatalog(id: string | number, payload: PatchSystemCatalogPayload): Promise<PatchSystemCatalogResponse> {
  const res = await axios.request<PatchSystemCatalogResponse>({
    method: HttpMethods.PATCH,
    url: `${BASE_PATH}/system-catalog/${id}`,
    data: payload,
  });
  return res.data;
}

/** Backend os.routes.ts: GET /system-pending — list pending systems submitted from public flow. */
export type SystemPendingStatus = 'pending' | 'approved' | 'merged' | 'rejected';

export type SystemPendingListItem = {
  id: number;
  submittedName: string;
  submittedVendor?: string | null;
  submittedNotes?: string | null;
  status: SystemPendingStatus;
  approvedCatalogId?: number | null;
  resolutionNotes?: string | null;
};

export type GetSystemPendingListResponse = {
  data: SystemPendingListItem[];
};

export async function getSystemPendingList(params?: { status?: SystemPendingStatus }): Promise<GetSystemPendingListResponse> {
  const res = await axios.request<GetSystemPendingListResponse>({
    method: HttpMethods.GET,
    url: `${BASE_PATH}/system-pending`,
    params: params ?? undefined,
  });
  return res.data;
}

/** Backend os.routes.ts: PATCH /system-pending/:id/{approve|merge|reject} — moderate pending system submissions. */
export type PatchSystemPendingPayload = {
  status: SystemPendingStatus;
  approvedCatalogId?: number;
  resolutionNotes?: string;
  category?: string;
  aliases?: string[];
};

export type PatchSystemPendingResponse = {
  data: SystemPendingListItem;
};

export async function patchSystemPending(id: string | number, payload: PatchSystemPendingPayload): Promise<PatchSystemPendingResponse> {
  const action =
    payload.status === 'approved' ? 'approve' : payload.status === 'merged' ? 'merge' : payload.status === 'rejected' ? 'reject' : null;

  if (!action) {
    throw new Error('Unsupported pending moderation status');
  }

  if (action === 'merge' && payload.approvedCatalogId == null) {
    throw new Error('approvedCatalogId is required for merge moderation');
  }

  const requestBody: Record<string, unknown> = {
    resolutionNotes: payload.resolutionNotes,
  };

  if (action === 'approve') {
    if (payload.approvedCatalogId != null) {
      requestBody.catalogId = payload.approvedCatalogId;
    }
    if (payload.category != null) {
      requestBody.category = payload.category;
    }
    if (payload.aliases != null) {
      requestBody.aliases = payload.aliases;
    }
  }

  if (action === 'merge') {
    requestBody.catalogId = payload.approvedCatalogId;
  }

  const res = await axios.request<PatchSystemPendingResponse>({
    method: HttpMethods.PATCH,
    url: `${BASE_PATH}/system-pending/${id}/${action}`,
    data: requestBody,
  });
  return res.data;
}
